import React, { useEffect, useRef, useState } from 'react';

const STORAGE_KEY = 'ft_consent_v1';

// Withdrawing consent has to be as easy as giving it, and until now there was
// no way to do either twice: Accept and Reject both stored a choice and the
// banner never came back, on any page. This is the way back.
export const CONSENT_REOPEN_EVENT = 'ft:consent-reopen';

/** Reopen the banner so a stored choice can be changed. */
export const openCookieSettings = () => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(CONSENT_REOPEN_EVENT));
};

// Google tags read the choice from Consent Mode, not from this component. The
// matching replay in public/index.html reads the same key synchronously on the
// next visit, so this only ever has to handle the first one.
const applyConsent = (value) => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  window.gtag('consent', 'update', {
    ad_storage: value,
    analytics_storage: value,
    ad_user_data: value,
    ad_personalization: value,
  });
};

const readStored = () => {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch (e) {
    // Private mode, or a browser set to block site data. Treat it as unanswered
    // rather than as consent.
    return null;
  }
};

const CookieConsent = () => {
  // Never true on the first render. The prerenderer snapshots that render into
  // all 79 static pages, and baking the banner into the HTML would show it to
  // visitors who had already answered, for as long as it took React to hydrate.
  const [visible, setVisible] = useState(false);
  const panelRef = useRef(null);

  useEffect(() => {
    const stored = readStored();
    if (stored !== 'granted' && stored !== 'denied') setVisible(true);
    const reopen = () => setVisible(true);
    window.addEventListener(CONSENT_REOPEN_EVENT, reopen);
    return () => window.removeEventListener(CONSENT_REOPEN_EVENT, reopen);
  }, []);

  // The banner is fixed to the bottom, so whatever is behind it cannot be
  // reached. Measured at 390px before this: it covered 159px of an 844px
  // viewport and sat on top of three Copy buttons, three Edit buttons and four
  // navigation links — on a page whose whole purpose is copying. Hold that much
  // space open at the end of the document while it is showing.
  useEffect(() => {
    if (!visible) return undefined;
    const apply = () => {
      const h = panelRef.current ? panelRef.current.getBoundingClientRect().height : 0;
      document.body.style.paddingBottom = h ? `${Math.ceil(h)}px` : '';
    };
    apply();
    window.addEventListener('resize', apply);
    return () => {
      window.removeEventListener('resize', apply);
      document.body.style.paddingBottom = '';
    };
  }, [visible]);

  const choose = (value) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, value);
    } catch (e) {
      // The choice still applies to this page view; it just will not persist.
    }
    applyConsent(value);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      ref={panelRef}
      data-prerender-omit=""
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-consent-title"
      className="fixed inset-x-0 bottom-0 z-[60] border-t border-[var(--color-border)] bg-[var(--color-card)] p-4 shadow-[0_-4px_24px_rgba(0,0,0,0.12)]"
    >
      <div className="site-container flex flex-col gap-3 md:flex-row md:items-center md:justify-between md:gap-6">
        <div className="min-w-0">
          <p id="cookie-consent-title" className="text-sm font-black text-[var(--color-text)]">
            Cookies on FontifyText
          </p>
          <p className="mt-1 text-sm leading-6 text-[var(--color-muted)]">
            We use cookies for analytics and advertising. The font generator itself
            works either way.{' '}
            <a
              href="/cookie-policy/"
              className="font-semibold text-[var(--color-primary)] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]"
            >
              Cookie Policy
            </a>
          </p>
        </div>

        <div className="flex shrink-0 gap-2">
          {/* Reject is listed first and styled no less prominently than accept,
              which is what a valid consent choice requires. */}
          <button
            type="button"
            onClick={() => choose('denied')}
            className="flex-1 rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] px-5 py-2.5 text-sm font-bold text-[var(--color-text)] transition hover:bg-[var(--color-card)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)] md:flex-none"
          >
            Reject
          </button>
          <button
            type="button"
            onClick={() => choose('granted')}
            className="flex-1 rounded-xl bg-[var(--color-primary)] px-5 py-2.5 text-sm font-bold text-white transition hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)] focus-visible:ring-offset-2 md:flex-none"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
